<template>
  <div :class="bem()">
    <div>
      <h6 :class="bem('section-title')">
        {{ props.keyFeaturesTitle || 'Key Features' }}
        <ToolTip
          v-if="props.keyFeaturesTooltip"
          arrow-position="left"
          :content="props.keyFeaturesTooltip"
        />
      </h6>
      <TechSpecsList :customised-product="props.customisedProduct" />
    </div>
    <AttributeRenderer :product-attributes="calculatedGenericProductAttributeSections" />
    <AttributeRenderer v-if="conditionAttributes.length" :product-attributes="conditionAttributes" />
  </div>
</template>

<script setup lang="ts">
import type { CircularRating, ProductAttribute } from '~/shared/types'

interface AccordionPerformanceProductAttributesProps {
  productAttribute: ProductAttribute
  customisedProduct: any
  keyFeaturesTitle: string
  keyFeaturesTooltip: string
  conditionsTitle: string
  conditionsTooltip: string
}

const props = defineProps<AccordionPerformanceProductAttributesProps>()

const bem = useBEM('AccordionPerformanceProductAttributes')

function getNightConditionVltRating(vlt: number) {
  if (vlt >= 89) {
    return 10
  } else if (vlt >= 79) {
    return 9
  } else if (vlt >= 44) {
    return 7
  }

  return 1
}

function getShowerConditionVltRating (vlt: number) {
  if (vlt >= 38) {
    return 10
  } else if (vlt >= 20) {
    return 8
  } else if (vlt >= 15) {
    return 6
  }

  return 4
}

function getCloudConditionVltRating(vlt: number) {
  if (vlt >= 38) {
    return 10
  } else if (vlt >= 20) {
    return 8
  } else if (vlt >= 15) {
    return 7
  }

  return 6
}

function getFogConditionVltRating (vlt: number) {
  /**
   *
   * Iris colours are specifically better in fog at these levels
   * and fall in between < 89 and > 40 VLT values.
   *
   **/
  if (vlt < 89 && vlt > 40) {
    return 10
  } else if (vlt >= 89) {
    return 8
  } else if (vlt < 40 && vlt > 15) {
    return 6
  } else if (vlt < 15) {
    return 3
  }
}

function getSunCloudConditionVltRating (vlt: number) {
  if (vlt === 100) {
    return 1
  } else if (vlt >= 38) {
    return 6
  } else if (vlt >= 20) {
    return 7
  } else if (vlt >= 16) {
    return 8
  }

  return 9
}

function getSunConditionVltRating (vlt: number) {
  if (vlt === 100) {
    return 1
  } else if (vlt >= 38) {
    return 5
  }  else if (vlt >= 15) {
    return 8
  } else if (vlt <= 14) {
    return 10
  }
}

const conditionRatings = computed(() => {
  if(!props.customisedProduct.lensChoice) return {}

  let calculatedVlt = props.customisedProduct.lensChoice.vlt

  if(calculatedVlt.includes('-')) {
    calculatedVlt = Number(calculatedVlt.split('-')[1])
  }

  return {
    snow: {
      rating: getShowerConditionVltRating(calculatedVlt)
    },
    showers: {
      rating: getShowerConditionVltRating(calculatedVlt)
    },
    cloudy: {
      rating: getCloudConditionVltRating(calculatedVlt)
    },
    fog: {
      rating: getFogConditionVltRating(calculatedVlt)
    },
    sunCloud: {
      rating: getSunCloudConditionVltRating(calculatedVlt)
    },
    sun: {
      rating: getSunConditionVltRating(calculatedVlt)
    },
    night: {
      rating: getNightConditionVltRating(calculatedVlt)
    }
  }
})

const conditionAttributes = computed(() => {
  if (props.customisedProduct.isLifestyle || !Object.keys(conditionRatings.value).length) return []

  return [{
    categorySections: [{
      sectionHeading: 'Conditions',
      sectionTooltip: props.conditionsTooltip,
      attributes: [
        (props.customisedProduct.isSnow ? {
          id: 'snow-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.snow.rating,
          label: 'Snow',
          icon: {
            icon: 'snow'
          }
        } as CircularRating : {}),
        (props.customisedProduct.isPace || props.customisedProduct.isLifestyle ? {
          id: 'showers-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.showers.rating,
          label: 'Showers',
          icon: {
            icon: 'showers'
          }
        } as CircularRating : {}),
        {
          id: 'cloud-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.cloudy.rating,
          label: 'Cloud',
          icon: {
            icon: 'cloudy'
          }
        } as CircularRating,
        {
          id: 'cloud-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.sunCloud.rating,
          label: 'Partly Cloudy',
          icon: {
            icon: 'day-cloudy'
          }
        } as CircularRating,
        {
          id: 'sun-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.sun.rating,
          label: 'Sun',
          icon: {
            icon: 'day-sunny'
          }
        } as CircularRating,
        (props.customisedProduct.isPace ? {
          id: 'fog-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.fog.rating,
          label: 'Fog',
          icon: {
            icon: 'fog'
          }
        } as CircularRating : {}),
        (props.customisedProduct.isPace && props.customisedProduct.lensChoice?.vlt >= 89 ? {
          id: 'night-condition-rating',
          type: 'circular_rating',
          rating: conditionRatings.value.night.rating,
          label: 'Night',
          icon: {
            icon: 'night-clear'
          }
        } as CircularRating : {}),
      ].filter(a => a.type)
    }]
  }]
})

const calculatedGenericProductAttributeSections = computed(() => {
  return props.productAttribute.productAttributeCategories.flatMap((attributeCategory) => {
    return {
      categoryHeading: attributeCategory.categoryHeading,
      categorySections: attributeCategory.productAttributeCategorySections.map((attributeCategorySection) => {
        return {
          sectionHeading: attributeCategorySection.sectionHeading,
          sectionTooltip: attributeCategorySection.sectionTooltip,
          attributes: attributeCategorySection.categoryAttributes,
        }
      })
    }
  })
})
</script>

<style lang="postcss" scoped>
.AccordionPerformanceProductAttributes {
  @apply flex flex-col gap-y-12;
}

.AccordionPerformanceProductAttributes__section-title {
  @apply heading-6 flex mb-3;
}
</style>